// 계약 관리 — 휴지통(C2c1): 휴지통으로 보낸 계약 목록 · 검색(화면에서만) · «복원»(번호가 겹치면 새 번호 창)
//   · «완전 삭제»(시스템 관리자 · 계약번호 정확 재입력).
// 🔴 완전 삭제는 되돌릴 수 없다(계약 행·원본 파일이 지워지고 이력만 남는다).
// 숨김은 편의이고 막는 것은 서버다(require_contract_level — 휴지통·복원 = 편집 · 완전 삭제 = 시스템 관리자).
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../../components/AppShell';
import { contractsAPI } from '../../api/client';
import { alertDialog, confirmDialog, toast } from '../../components/AppDialog';
import { COLORS, RADIUS, btn, card, input } from '../../utils/uiStyles';
import { fmtKstDateTime } from '../../utils/kstTime';
import { CATEGORY_LABELS, errorText, labelOf } from './contractLabels';

const HEADERS = ['계약번호', '분류', '계약명', '상대방', '지운 사람', '지운 때', '작업'];
const DENIED = '휴지통은 편집 등급만 볼 수 있습니다. 등급이 바뀌었으면 새로고침해 주세요.';
const NUMBER_LENGTH = '계약번호는 1~40자입니다.';   // 서버 restore 400 과 같은 문구
const th = { padding: '9px', textAlign: 'left', fontSize: '12px', color: COLORS.textSub, whiteSpace: 'nowrap', borderBottom: `2px solid ${COLORS.border}` };
const td = { padding: '8px 9px', fontSize: '13px', borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap' };
const text = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));
const titleOf = (item) => [item?.contract_no, item?.contract_name].filter(Boolean).join(' ');

// 작은 창 틀 — Esc·바깥 클릭·«취소»로 닫는다(요청 중이면 닫기 무시) · Enter 는 form 제출.
//   Tab·Shift+Tab 은 창 안(켜진 요소만)에서 돈다.
const FOCUSABLE = 'button, input, select, textarea, [href], [tabindex]:not([tabindex="-1"])';

function SmallDialog({ label, busy, onClose, onSubmit, children }) {
  const close = () => { if (!busy) onClose(); };
  return (
    <div
      role="presentation"
      onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: '20px', background: 'rgba(17,24,39,.45)',
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-label={label}
        noValidate
        onSubmit={(event) => { event.preventDefault(); onSubmit(); }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            close();
            return;
          }
          if (event.key !== 'Tab') return;
          const nodes = Array.from(event.currentTarget.querySelectorAll(FOCUSABLE)).filter((el) => !el.disabled);
          if (nodes.length === 0) {
            event.preventDefault();
            return;
          }
          const first = nodes[0];
          const last = nodes[nodes.length - 1];
          const active = document.activeElement;
          // 두 끝에서만 반대 끝으로 돌린다 — 그 밖에는 브라우저 기본 이동.
          if (!event.shiftKey && active === last) {
            event.preventDefault();
            first.focus();
          } else if (event.shiftKey && active === first) {
            event.preventDefault();
            last.focus();
          }
        }}
        style={{
          width: 'min(460px, 100%)', boxSizing: 'border-box', padding: '22px',
          borderRadius: RADIUS.lg, background: COLORS.surface, boxShadow: '0 18px 48px rgba(17,24,39,.2)',
        }}
      >
        <h2 style={{ margin: '0 0 12px', color: COLORS.text, fontSize: '17px' }}>{label}</h2>
        {children}
      </form>
    </div>
  );
}

const dialogText = { margin: '0 0 10px', color: COLORS.text, fontSize: '13px', lineHeight: 1.6, wordBreak: 'break-word' };
const dialogError = { margin: '10px 0 0', color: COLORS.dangerText, fontSize: '13px' };
const dialogButtons = { display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' };

// 복원 409 뒤 — 새 번호를 넣어 복원한다(입력 칸 처음 값 = 옛 번호).
function RenumberDialog({ item, message, error, busy, onSubmit, onClose }) {
  const [value, setValue] = useState(item.contract_no || '');
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  return (
    <SmallDialog label="번호를 바꿔 복원" busy={busy} onClose={onClose} onSubmit={() => onSubmit(value)}>
      <p style={dialogText}>{message}</p>
      <p style={dialogText}>새 계약번호를 넣어 주세요.</p>
      <input
        ref={inputRef}
        type="text"
        aria-label="새 계약번호"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        style={input}
      />
      {error && <div role="alert" style={dialogError}>{error}</div>}
      <div style={dialogButtons}>
        <button type="button" onClick={() => { if (!busy) onClose(); }} disabled={busy} style={btn('secondary')}>취소</button>
        <button type="submit" disabled={busy} style={{ ...btn('primary'), opacity: busy ? 0.65 : 1 }}>복원</button>
      </div>
    </SmallDialog>
  );
}

// 완전 삭제 — 입력이 계약번호와 정확히 같을 때만 단추가 켜진다(공백 정리 없이 · 서버 비교와 같게).
function PurgeDialog({ item, error, busy, onSubmit, onClose }) {
  const [value, setValue] = useState('');
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  const matches = typeof item.contract_no === 'string' && value === item.contract_no;
  const disabled = busy || !matches;
  return (
    <SmallDialog label="완전 삭제 확인" busy={busy} onClose={onClose} onSubmit={() => { if (!disabled) onSubmit(value); }}>
      <p style={dialogText}>
        「{titleOf(item)}」을 완전 삭제합니다. 계약과 원본 파일이 지워지고 되돌릴 수 없습니다(변경 이력은 남습니다).
        확인하려면 계약번호를 그대로 입력해 주세요.
      </p>
      <input
        ref={inputRef}
        type="text"
        aria-label="확인용 계약번호"
        placeholder={item.contract_no || ''}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        style={input}
      />
      {error && <div role="alert" style={dialogError}>{error}</div>}
      <div style={dialogButtons}>
        <button type="button" onClick={() => { if (!busy) onClose(); }} disabled={busy} style={btn('secondary')}>취소</button>
        <button type="submit" disabled={disabled}
          style={{ ...btn('primary'), background: COLORS.danger, opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
          완전 삭제
        </button>
      </div>
    </SmallDialog>
  );
}

export default function ContractTrashPage() {
  const navigate = useNavigate();
  // 등급 — null = 판정 전(거부 문구를 그리지 않는다) · true = 편집 · false = 열람(등급 읽기 실패도 열람).
  const [canEdit, setCanEdit] = useState(null);
  const [canPurge, setCanPurge] = useState(false);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [forbidden, setForbidden] = useState(false);
  const [q, setQ] = useState('');
  const [actions, setActions] = useState(0);              // 도는 복원·완전 삭제 요청 수(두 창 포함)
  const [renumber, setRenumber] = useState(null);        // 번호 바꾸기 창 { item, message }
  const [renumberError, setRenumberError] = useState('');
  const [purgeTarget, setPurgeTarget] = useState(null);  // 완전 삭제 창의 계약
  const [purgeError, setPurgeError] = useState('');
  const mountedRef = useRef(true);
  const listSeqRef = useRef(0);
  const loadingRef = useRef(false);                     // 목록 읽기가 도는 중인가(가장 나중 요청 기준)
  const actionsRef = useRef(0);

  // 목록 읽기 — 요청마다 번호를 올리고, 가장 나중 요청의 응답만 목록을 바꾼다(늦은 응답 버리기).
  const load = () => {
    const seq = listSeqRef.current + 1;
    listSeqRef.current = seq;
    const isCurrent = () => mountedRef.current && listSeqRef.current === seq;
    setLoading(true);
    loadingRef.current = true;
    contractsAPI.trashList()
      .then((r) => {
        if (!isCurrent()) return;
        setItems(Array.isArray(r.data?.items) ? r.data.items : []);
        setLoadError('');
        setForbidden(false);
      })
      .catch(async (err) => {
        if (err?.response?.status === 403) {
          if (isCurrent()) setForbidden(true);
          return;
        }
        const message = await errorText(err, '휴지통을 불러오지 못했습니다.');
        if (!isCurrent()) return;
        setItems(null);
        setLoadError(message);
      })
      .finally(() => {
        if (isCurrent()) {
          loadingRef.current = false;
          setLoading(false);
        }
      });
  };

  useEffect(() => {
    mountedRef.current = true;
    contractsAPI.me()
      .then((r) => {
        if (!mountedRef.current) return;
        setCanEdit(r.data?.can?.edit === true);
        setCanPurge(r.data?.can?.purge === true);
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setCanEdit(false);
        setCanPurge(false);
      });
    load();
    return () => {
      mountedRef.current = false;
      listSeqRef.current += 1;   // 화면을 떠난 뒤 온 응답은 버린다
    };
  }, []);

  // 복원·완전 삭제 요청이 하나라도 도는 동안 표의 작업 단추를 전부 끈다(목록 다시 읽기는 끄지 않는다).
  const begin = () => { actionsRef.current += 1; setActions(actionsRef.current); };
  const end = () => { actionsRef.current -= 1; if (mountedRef.current) setActions(actionsRef.current); };
  const busy = actions > 0;
  // 행을 뺄 때 목록 읽기가 돌고 있으면 그 응답(뺀 행이 아직 든 목록일 수 있다)을 버리고 다시 읽는다.
  const removeRow = (id) => {
    setItems((prev) => (Array.isArray(prev) ? prev.filter((it) => it.id !== id) : prev));
    if (loadingRef.current && mountedRef.current) load();
  };

  // 다른 사람이 먼저 복원·삭제했다 — 알리고 목록을 다시 읽는다.
  const goneThenReload = async (err) => {
    const message = await errorText(err, '휴지통에서 계약을 찾을 수 없습니다.');
    if (!mountedRef.current) return;
    await alertDialog(message);
    if (mountedRef.current) load();
  };

  const restoreItem = async (item) => {
    if (actionsRef.current > 0) return;
    if (!(await confirmDialog(`「${titleOf(item)}」을 복원할까요?`))) return;
    if (!mountedRef.current || actionsRef.current > 0) return;
    begin();
    let failure = null;
    try {
      await contractsAPI.restore(item.id);
    } catch (err) {
      failure = err;
    } finally {
      end();
    }
    if (!mountedRef.current) return;
    if (!failure) {
      removeRow(item.id);
      toast('복원했습니다.');
      return;
    }
    const status = failure?.response?.status;
    if (status === 409) {
      const message = await errorText(failure, '같은 계약번호가 있습니다 — 번호를 바꿔 복원해 주세요.');
      if (!mountedRef.current) return;
      setRenumberError('');
      setRenumber({ item, message });
      return;
    }
    if (status === 404) {
      await goneThenReload(failure);
      return;
    }
    const message = await errorText(failure, '복원하지 못했습니다.');
    if (!mountedRef.current) return;
    await alertDialog(message);
  };

  const submitRenumber = async (value) => {
    const target = renumber?.item;
    if (!target || actionsRef.current > 0) return;
    const trimmed = String(value ?? '').trim();
    const length = Array.from(trimmed).length;   // 서버(파이썬 len)와 같게 글자 수로 센다
    if (length < 1 || length > 40) {
      setRenumberError(NUMBER_LENGTH);
      return;
    }
    setRenumberError('');
    begin();
    let failure = null;
    try {
      await contractsAPI.restore(target.id, trimmed);
    } catch (err) {
      failure = err;
    } finally {
      end();
    }
    if (!mountedRef.current) return;
    if (!failure) {
      setRenumber(null);
      removeRow(target.id);
      toast(`복원했습니다. 계약번호: ${trimmed}`);
      return;
    }
    if (failure?.response?.status === 404) {
      setRenumber(null);
      await goneThenReload(failure);
      return;
    }
    // 400·409·403·500 — 창 안에 문구를 보이고 입력은 그대로 둔다.
    const message = await errorText(failure, '복원하지 못했습니다.');
    if (mountedRef.current) setRenumberError(message);
  };

  const openPurge = (item) => {
    if (actionsRef.current > 0) return;
    setPurgeError('');
    setPurgeTarget(item);
  };

  const submitPurge = async (value) => {
    const target = purgeTarget;
    if (!target || value !== target.contract_no || actionsRef.current > 0) return;
    setPurgeError('');
    begin();
    let failure = null;
    try {
      await contractsAPI.purge(target.id, value);
    } catch (err) {
      failure = err;
    } finally {
      end();
    }
    if (!mountedRef.current) return;
    if (!failure) {
      setPurgeTarget(null);
      removeRow(target.id);
      toast('완전 삭제했습니다.');
      return;
    }
    const message = await errorText(failure, '완전 삭제하지 못했습니다.');
    if (!mountedRef.current) return;
    setPurgeError(message);
    // 남이 먼저 복원했거나 번호를 바꿔 복원했다 — 목록을 다시 읽는다.
    const status = failure?.response?.status;
    if (status === 400 || status === 404) load();
  };

  const all = Array.isArray(items) ? items : [];
  const term = q.trim().toLowerCase();
  const shown = term
    ? all.filter((it) => [it.contract_no, it.contract_name, it.counterparty]
      .some((v) => typeof v === 'string' && v.toLowerCase().includes(term)))
    : all;
  const levelKnown = canEdit !== null;
  const denied = levelKnown && (!canEdit || forbidden);
  const showLoading = !levelKnown || (canEdit && !forbidden && loading);

  const renderBody = () => {
    if (!levelKnown) return null;
    if (denied) {
      return <div style={{ ...card, color: COLORS.textSub, fontSize: '13px' }}>{DENIED}</div>;
    }
    if (loadError) {
      return (
        <div role="alert" style={{ ...card, background: COLORS.dangerSoft, color: COLORS.dangerText, display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span>{loadError}</span>
          <button type="button" onClick={load} style={btn('secondary', 'sm')}>다시 시도</button>
        </div>
      );
    }
    if (!Array.isArray(items)) return null;
    return (
      <>
        <section aria-label="휴지통 검색 조건" style={{ ...card, padding: '12px 14px', marginBottom: '12px', display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
          <input type="search" aria-label="휴지통 검색" placeholder="계약번호·계약명·상대방" value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ ...input, width: '260px' }} />
          <span style={{ fontSize: '12px', color: COLORS.textSub }}>{`${shown.length.toLocaleString()}건(전체 ${all.length.toLocaleString()}건)`}</span>
        </section>
        <section style={{ ...card, padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table aria-label="휴지통" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
              <thead><tr>{HEADERS.map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {shown.map((it) => (
                  <tr key={it.id} data-testid="contract-trash-row">
                    <td style={td}>{text(it.contract_no)}</td>
                    <td style={td}>{labelOf(CATEGORY_LABELS, it.category)}</td>
                    <td style={td}>{text(it.contract_name)}</td>
                    <td style={td}>{text(it.counterparty)}</td>
                    <td style={td}>{text(it.deleted_by_name)}</td>
                    <td style={td}>{fmtKstDateTime(it.deleted_at) || '—'}</td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button type="button" onClick={() => restoreItem(it)} disabled={busy}
                          style={{ ...btn('secondary', 'sm'), opacity: busy ? 0.5 : 1, cursor: busy ? 'not-allowed' : 'pointer' }}>복원</button>
                        {canPurge && (
                          <button type="button" onClick={() => openPurge(it)} disabled={busy}
                            style={{ ...btn('danger', 'sm'), opacity: busy ? 0.5 : 1, cursor: busy ? 'not-allowed' : 'pointer' }}>완전 삭제</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {all.length === 0 && (
                  <tr><td colSpan={HEADERS.length} style={{ ...td, color: COLORS.textMuted, textAlign: 'center', padding: '24px' }}>휴지통이 비어 있습니다.</td></tr>
                )}
                {all.length > 0 && shown.length === 0 && (
                  <tr><td colSpan={HEADERS.length} style={{ ...td, color: COLORS.textMuted, textAlign: 'center', padding: '24px' }}>검색에 맞는 계약이 없습니다.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </>
    );
  };

  return (
    <AppShell app="contracts">
      <main style={{ flex: 1, minWidth: 0, height: 'var(--gw-app-h, 100dvh)', overflow: 'auto', background: COLORS.bg }}>
        <div style={{ padding: '20px 24px', maxWidth: '1450px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
            <h2 style={{ margin: 0, fontSize: '20px' }}>휴지통</h2>
            <button type="button" onClick={() => navigate('/contracts')} style={btn('secondary', 'sm')}>← 계약 목록</button>
            {showLoading && <span role="status" style={{ color: COLORS.textMuted, fontSize: '12px' }}>불러오는 중…</span>}
          </div>
          {renderBody()}
        </div>
      </main>
      {renumber && (
        <RenumberDialog
          key={renumber.item.id}
          item={renumber.item}
          message={renumber.message}
          error={renumberError}
          busy={busy}
          onSubmit={submitRenumber}
          onClose={() => setRenumber(null)}
        />
      )}
      {purgeTarget && (
        <PurgeDialog
          key={purgeTarget.id}
          item={purgeTarget}
          error={purgeError}
          busy={busy}
          onSubmit={submitPurge}
          onClose={() => setPurgeTarget(null)}
        />
      )}
    </AppShell>
  );
}
